'use no memo';
/* eslint-disable react-hooks/exhaustive-deps, react-hooks/rules-of-hooks, react-hooks/react-compiler */

/**
 * We intentionally break the rules of React here in order to facilitate a migration to
 * Syncronous flux. This will not work if a subtree is switched from Sync to Async at
 * runtime which we should not do.
 */
import React from 'react';
// @ts-expect-error - TS7016 - Could not find a declaration file for module 'react-recompose'.
import {getDisplayName, shallowEqual} from 'react-recompose';
import {SyncFluxContext} from '../Flux';
import {emptyObject, emptyArray} from '@packages/utilities/fp/utils';
import {useSyncExternalStoreWithSelector} from 'use-sync-external-store/shim/with-selector';
import {weakMemo} from '@packages/utilities/memo';

import {TFlux, type Dispatch} from '../types';

type MapStateToProps<Stores, OwnProps, Props> = (
  stores: Stores,
  ownProps: OwnProps
) => Props;
type MapDispatchToProps<D, LegacyCreators, Props> =
  | ((dispatch: D) => Props)
  | {
      __UNSAFE__mapActionsToProps(dispatch: D, creators: LegacyCreators): Props;
    };

type Options = {
  sync?: boolean;
};

const createActionCreators = weakMemo(
  <
    Action,
    LegacyCreators extends Record<string, any>,
    Props,
    Stores extends Record<string, any>,
  >(
    mapDispatchToProps: MapDispatchToProps<
      Dispatch<Action>,
      LegacyCreators,
      Props
    >
  ) =>
    weakMemo((flux: TFlux<Stores, any, LegacyCreators>): Props => {
      if (typeof mapDispatchToProps === 'function') {
        return mapDispatchToProps(flux.dispatch);
      }

      return mapDispatchToProps.__UNSAFE__mapActionsToProps(
        flux.dispatch,
        flux.creators
      );
    })
);

const createMapStateToPropsSelector = weakMemo(
  <FullState, OwnProps, Props>(
    mapStateToProps: MapStateToProps<FullState, OwnProps, Props>
  ) =>
    weakMemo((props: OwnProps) => (state: FullState): Props => {
      return mapStateToProps(state, props);
    })
);

const createGetInitialState = weakMemo(
  <
    FullState extends Record<string, any>,
    OwnProps,
    Props,
    LegacyCreators extends Record<string, any>,
  >(
    mapStateToProps: MapStateToProps<FullState, OwnProps, Props>
  ) =>
    weakMemo((flux: TFlux<FullState, any, LegacyCreators>) =>
      weakMemo((props: React.MutableRefObject<OwnProps>) => () => {
        return mapStateToProps(flux.state, props.current);
      })
    )
);

const noop = () => emptyObject;
const noopVoid = () => {};

const createOnStateChange = weakMemo(
  <FullState, State, OwnProps>(
    mapStateToProps: (fullState: FullState, ownProps: OwnProps) => State
  ) =>
    weakMemo((flux: {state: FullState}) =>
      weakMemo((stateRef: React.MutableRefObject<State>) =>
        weakMemo((setState: React.Dispatch<React.SetStateAction<State>>) =>
          weakMemo((propsRef: React.MutableRefObject<OwnProps>) => {
            return () => {
              const previousState = stateRef.current;
              const nextState: State = mapStateToProps(
                flux.state,
                propsRef.current
              );
              if (shallowEqual(nextState, previousState)) return;
              stateRef.current = nextState;
              setState((_previousState) => nextState);
            };
          })
        )
      )
    )
);

const createFluxSubscription = weakMemo(
  <Stores extends Record<any, any>, LegacyCreators extends Record<any, any>>(
    flux: TFlux<Stores, any, LegacyCreators>
  ) =>
    weakMemo((onStateChange: () => void) => {
      return () => flux.onChange(onStateChange);
    })
);

export default function createContainer<
  Stores extends Record<any, any>,
  LegacyCreators extends Record<any, any>,
>(Context: React.Context<TFlux<Stores, any, LegacyCreators>>) {
  return function container<
    OwnProps = Record<string, any>,
    StateProps = Record<never, never>,
    DispatchProps = Record<never, never>,
  >(
    mapStateToProps?: MapStateToProps<Stores, OwnProps, StateProps>,
    mapDispatchToProps?: MapDispatchToProps<any, LegacyCreators, DispatchProps>,
    {sync = false}: Options = {}
  ) {
    const createActionCreatorsWithFlux = mapDispatchToProps
      ? createActionCreators<any, LegacyCreators, DispatchProps, Stores>(
          mapDispatchToProps
        )
      : null;
    const createMapStateToPropsSelectorWithProps = mapStateToProps
      ? createMapStateToPropsSelector(mapStateToProps)
      : null;

    const createGetInitialStateWithFlux = mapStateToProps
      ? createGetInitialState<Stores, OwnProps, StateProps, any>(
          mapStateToProps
        )
      : null;

    const createOnStateChangeWithFlux = mapStateToProps
      ? createOnStateChange<Stores, StateProps, OwnProps>(mapStateToProps)
      : null;
    function containerDecorator(
      Component: React.ComponentType<
        OwnProps &
          StateProps &
          DispatchProps & {flux: TFlux<Stores, any, LegacyCreators>}
      >
    ) {
      // Inner uses `forwardRef` so consumers (e.g. `tooltipTrigger` via
      // `cloneElement({ref})`) can reach the wrapped Component's underlying
      // DOM node. Without this, `React.memo(fn)` swallows the ref and the
      // wrapped Component never receives it — the silent failure mode that
      // produced misplaced tooltips on connected components like
      // `StyleRuleToken` (see SHSVS-8448 review). For the ref to actually
      // land on a DOM element, the wrapped Component must itself be a
      // `forwardRef`. Components that aren't forwardRef silently drop the
      // ref (pre-existing behavior) and React emits its usual warning.
      const Container = React.memo(
        React.forwardRef<unknown, OwnProps>((props, ref) => {
          const flux = React.useContext(Context);
          const isSync = React.useContext(SyncFluxContext) || sync;
          const actions: DispatchProps =
            createActionCreatorsWithFlux && flux
              ? createActionCreatorsWithFlux(flux)
              : (noop as DispatchProps);

          type Selector = (stores: Stores) => StateProps;
          const selector: Selector = createMapStateToPropsSelectorWithProps
            ? createMapStateToPropsSelectorWithProps(props)
            : (noop as Selector);

          if (isSync) {
            const syncState = useSyncExternalStoreWithSelector(
              flux.onChangeSync,
              flux.getState,
              flux.getState,
              selector,
              shallowEqual
            );
            return (
              <Component
                ref={ref}
                {...{flux, ...props, ...syncState, ...actions}}
              />
            );
          } else {
            const propsRef = React.useRef(props);
            const [state, setState] = React.useState<StateProps>(
              flux && createGetInitialStateWithFlux
                ? createGetInitialStateWithFlux(flux)(propsRef)
                : (emptyObject as StateProps)
            );
            const stateRef = React.useRef(state);
            stateRef.current = state;

            const onStateChange =
              flux && createOnStateChangeWithFlux
                ? createOnStateChangeWithFlux(flux)(stateRef)(setState)(
                    propsRef
                  )
                : noopVoid;

            propsRef.current = props;

            React.useLayoutEffect(
              flux ? createFluxSubscription(flux)(onStateChange) : noopVoid,
              emptyArray
            );

            return (
              <Component
                ref={ref}
                {...{flux, ...props, ...state, ...actions}}
              />
            );
          }
        })
      );

      Container.displayName = `FluxContainer(${getDisplayName(Component)})`;

      return Container;
    }

    return containerDecorator;
  };
}
